import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { addMovie, updateMovie } from "../redux/slices/moviesSlice";
import { addUser, updateUser } from "../redux/slices/usersSlice";
import { addAnime, updateAnime } from "../redux/slices/animeSlice";

const AddEditModal = ({ isOpen, onClose, data, type, onMovieAdded }) => {
  // Lista predefinida de géneros disponibles
  const genreOptions = [
    "Accion",
    "Aventura",
    "Ciencia Ficcion",
    "Comedia",
    "Crimen",
    "Drama",
    "Misterio",
    "Musical",
    "Romance",
    "Terror",
    "Western",
  ];
  const [movieName, setMovieName] = useState("");
  const [genre, setGenre] = useState([]);
  const [description, setDescription] = useState("");
  const [banner, setBanner] = useState("");
  const [poster, setPoster] = useState("");
  const [srtUrl, setSrtUrl] = useState("");
  const [movieUrl, setMovieUrl] = useState("");
  const [fileSize, setFileSize] = useState("");
  const [mimeType, setMimeType] = useState("");
  const [runtime, setRuntime] = useState("");
  const [releaseYear, setReleaseYear] = useState("");
  const [seasons, setSeasons] = useState([]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");

  const dispatch = useDispatch();

  useEffect(() => {
    if (data) {
      if (type === "movie" || type === "anime") {
        setMovieName(data.name || "");
        // Convertir el string de género a array si es necesario
        const genreValue = data.genre || "";
        const genreArray = typeof genreValue === 'string' ? genreValue.split(", ") : (Array.isArray(genreValue) ? genreValue : []);
        setGenre(genreArray);
        setDescription(data.description || "");
        setBanner(data.banner || "");
        setPoster(data.poster || "");
        setSrtUrl(data.srtUrl || "");
        setMovieUrl(data.movieUrl || "");
        setFileSize(data.fileSize || "");
        setMimeType(data.mimeType || "");
        setRuntime(data.runtime || "");
        if (type === "anime") {
          setReleaseYear(data.releaseYear || new Date().getFullYear());
          setSeasons(data.seasons || []);
        }
      } else if (type === "user") {
        setName(data.name || "");
        setEmail(data.email || "");
        setPassword(data.password || "");
        setRole(data.role || "user");
      }
    }
  }, [data, type]);

  useEffect(() => {
    if (!isOpen) {
      setMovieName("");
      setGenre([]);
      setDescription("");
      setBanner("");
      setPoster("");
      setSrtUrl("");
      setMovieUrl("");
      setFileSize("");
      setMimeType("");
      setRuntime("");
      setReleaseYear("");
      setSeasons([]);
      setName("");
      setEmail("");
      setPassword("");
      setRole("user");
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (type === "movie") {
      const movieData = {
        name: movieName,
        // Convertir el array de géneros a string para almacenar en la base de datos
        genre: Array.isArray(genre) ? genre.join(", ") : genre,
        description,
        banner,
        poster: poster || "https://via.placeholder.com/300x450.png?text=Poster", // Valor por defecto
        srtUrl: srtUrl || "https://www.example.com/subtitles/demo.srt", // Valor por defecto
        movieUrl,
        // Convertir explícitamente a enteros para evitar problemas de validación
        fileSize: parseInt(fileSize, 10) || 0,
        mimeType,
        runtime: parseInt(runtime, 10) || 0,
      };

      console.log("Datos enviados al servidor:", movieData);

      if (data) {
        console.log("Actualizando película con ID:", data.id);
        dispatch(updateMovie({ id: data.id, movieData })).then((result) => {
          console.log("Resultado de actualización:", result);
          onClose();
        });
      } else {
        console.log("Creando nueva película");
        dispatch(addMovie(movieData)).then((result) => {
          console.log("Resultado de creación:", result);
          // Verificar si onMovieAdded existe antes de llamarlo
          if (onMovieAdded && typeof onMovieAdded === 'function') {
            onMovieAdded();
          }
          onClose();
        });
      }
    } else if (type === "anime") {
      // Preparar los datos del anime con sus temporadas y episodios
      const animeData = {
        name: movieName,
        genre: Array.isArray(genre) ? genre.join(", ") : genre,
        description,
        banner,
        poster: poster || "https://via.placeholder.com/300x450.png?text=Anime", // Valor por defecto
        releaseYear: parseInt(releaseYear, 10) || new Date().getFullYear(),
      };
      
      console.log("Datos de anime enviados al servidor:", animeData);
      
      if (data) {
        console.log("Actualizando anime con ID:", data.id);
        dispatch(updateAnime({ id: data.id, animeData })).then((result) => {
          console.log("Resultado de actualización de anime:", result);
          onClose();
        });
      } else {
        console.log("Creando nuevo anime");
        dispatch(addAnime(animeData)).then((result) => {
          console.log("Resultado de creación de anime:", result);
          onClose();
        });
      }
    } else if (type === "user") {
      const userData = { name, email, password, role };

      if (data) {
        console.log("Actualizando usuario con ID:", data.id);
        dispatch(updateUser({ id: data.id, userData })).then((result) => {
          console.log("Resultado de actualización:", result);
          onClose();
        });
      } else {
        console.log("Creando nuevo usuario");
        dispatch(addUser({ userData })).then((result) => {
          console.log("Resultado de creación:", result);
          onClose();
        });
      }
    }
  };

  return (
    isOpen && (
      <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/50">
        <div className="bg-gray-800 p-8 rounded-lg max-w-lg w-full max-h-[90vh] overflow-auto">
          <h2 className="text-2xl text-white mb-4">
            {type === "user" ? "Add/Edit User" : type === "anime" ? "Add/Edit Anime" : "Add/Edit Movie"}
          </h2>
          <form onSubmit={handleSubmit}>
            {type === "user" ? (
              <>
                <div>
                  <label className="text-white">Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="p-2 mt-2 w-full border rounded-lg bg-gray-700 text-white"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="text-white">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="p-2 mt-2 w-full border rounded-lg bg-gray-700 text-white"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="text-white">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="p-2 mt-2 w-full border rounded-lg bg-gray-700 text-white"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="text-white">Role</label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    required
                    className="p-2 mt-2 w-full border rounded-lg bg-gray-700 text-white"
                    autoComplete="off"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="text-white">Movie Name</label>
                  <input
                    type="text"
                    value={movieName}
                    onChange={(e) => setMovieName(e.target.value)}
                    required
                    className="p-2 mt-2 w-full border rounded-lg bg-gray-700 text-white"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="text-white">Géneros</label>
                  <div className="mt-2 p-2 border rounded-lg bg-gray-700 text-white max-h-40 overflow-y-auto">
                    {genreOptions.map((option) => (
                      <div key={option} className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          id={`genre-${option}`}
                          checked={genre.includes(option)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setGenre([...genre, option]);
                            } else {
                              setGenre(genre.filter(g => g !== option));
                            }
                          }}
                          className="mr-2"
                        />
                        <label htmlFor={`genre-${option}`} className="text-sm">{option}</label>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-xs text-gray-400">
                    Géneros seleccionados: {genre.join(", ") || "Ninguno"}
                  </div>
                </div>
                <div>
                  <label className="text-white">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                    className="p-2 mt-2 w-full border rounded-lg bg-gray-700 text-white"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="text-white">Banner</label>
                  <input
                    type="text"
                    value={banner}
                    onChange={(e) => setBanner(e.target.value)}
                    required
                    className="p-2 mt-2 w-full border rounded-lg bg-gray-700 text-white"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="text-white">Poster URL</label>
                  <input
                    type="text"
                    value={poster}
                    onChange={(e) => setPoster(e.target.value)}
                    placeholder="https://via.placeholder.com/300x450.png?text=Poster"
                    required
                    className="p-2 mt-2 w-full border rounded-lg bg-gray-700 text-white"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="text-white">Subtítulos URL (SRT)</label>
                  <input
                    type="text"
                    value={srtUrl}
                    onChange={(e) => setSrtUrl(e.target.value)}
                    placeholder="https://www.example.com/subtitles/demo.srt"
                    required
                    className="p-2 mt-2 w-full border rounded-lg bg-gray-700 text-white"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="text-white">Movie URL</label>
                  <input
                    type="text"
                    value={movieUrl}
                    onChange={(e) => setMovieUrl(e.target.value)}
                    required
                    className="p-2 mt-2 w-full border rounded-lg bg-gray-700 text-white"
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label className="text-white">File Size</label>
                  <input
                    type="number"
                    value={fileSize}
                    onChange={(e) => setFileSize(e.target.value)}
                    required
                    className="p-2 mt-2 w-full border rounded-lg bg-gray-700 text-white"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="text-white">MIME Type</label>
                  <input
                    type="text"
                    value={mimeType}
                    onChange={(e) => setMimeType(e.target.value)}
                    required
                    className="p-2 mt-2 w-full border rounded-lg bg-gray-700 text-white"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="text-white">Runtime (mins)</label>
                  <input
                    type="number"
                    value={runtime}
                    onChange={(e) => setRuntime(e.target.value)}
                    required
                    className="p-2 mt-2 w-full border rounded-lg bg-gray-700 text-white"
                    autoComplete="off"
                  />
                </div>
                {type === "anime" && (
                  <>
                    <div>
                      <label className="text-white">Release Year</label>
                      <input
                        type="number"
                        value={releaseYear}
                        onChange={(e) => setReleaseYear(e.target.value)}
                        required
                        className="p-2 mt-2 w-full border rounded-lg bg-gray-700 text-white"
                        autoComplete="off"
                      />
                    </div>
                    <div>
                      <label className="text-white">Seasons</label>
                      <div className="mt-2 p-2 border rounded-lg bg-gray-700 text-white max-h-40 overflow-y-auto">
                        {seasons.map((season, index) => (
                          <div key={index} className="flex items-center mb-2">
                            <input
                              type="text"
                              value={season.title}
                              onChange={(e) => {
                                const newSeasons = [...seasons];
                                newSeasons[index].title = e.target.value;
                                setSeasons(newSeasons);
                              }}
                              required
                              className="p-2 w-full border rounded-lg bg-gray-700 text-white"
                              autoComplete="off"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newSeasons = [...seasons];
                                newSeasons.splice(index, 1);
                                setSeasons(newSeasons);
                              }}
                              className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg"
                            >
                              Eliminar
                            </button>
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newSeasons = [...seasons];
                          newSeasons.push({ seasonNumber: seasons.length + 1, title: "", description: "", poster: "", releaseDate: "", episodes: [] });
                          setSeasons(newSeasons);
                        }}
                        className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg"
                      >
                        Agregar temporada
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

            <div className="flex gap-3 mt-4">
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg flex-1"
              >
                {data ? "Update" : "Add"} {type === "user" ? "User" : type === "anime" ? "Anime" : "Movie"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-lg flex-1"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  );
};

export default AddEditModal;
